import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Activity, Mail, Lock, LogIn, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated, user, loading } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Load remembered email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  // Check onboarding status and redirect
  const checkOnboardingAndRedirect = async (userData) => {
    // Only check onboarding for athletes and parents
    if (userData?.role === 'athlete' || userData?.role === 'parent') {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/onboarding/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!response.data.completed) {
          console.log('[Login] User needs onboarding, redirecting...');
          navigate('/onboarding', { replace: true });
          return;
        }
      } catch (err) {
        console.log('[Login] Could not check onboarding status:', err);
      }
    }
    
    // Default redirect to dashboard
    navigate('/', { replace: true });
  };

  // Redirect if already authenticated
  useEffect(() => {
    console.log('[Login] Auth state check - isAuthenticated:', isAuthenticated, 'user:', user?.email, 'loading:', loading);
    if (!loading && isAuthenticated && user) {
      console.log('[Login] Already authenticated, checking onboarding...');
      checkOnboardingAndRedirect(user);
    }
  }, [isAuthenticated, user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Handle remember me
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', formData.email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }

    try {
      const userData = await login(formData.email, formData.password);
      toast.success('Welcome back!');
      
      // Check onboarding status and redirect accordingly
      await checkOnboardingAndRedirect(userData);
      
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.detail || error.message || 'Login failed';
      toast.error(message);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_youth-athlete-lab/artifacts/pr7l13yi_Elite%20%284%29.png" 
            alt="BE ELITE APP" 
            className="h-32 mx-auto mb-4"
          />
          <p className="text-gray-400 mt-2">Sign in to your account</p>
        </div>

        {/* Login Form */}
        <div className="bg-zinc-900 rounded-2xl shadow-xl p-8 border border-zinc-800">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-12 pl-10 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                  placeholder="coach@example.com"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-gray-300 mb-2 block">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="h-12 pl-10 pr-12 bg-zinc-800 border-zinc-700 focus:border-[#02b5ff] focus:ring-[#02b5ff] text-white rounded-xl"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-[#02b5ff] focus:ring-[#02b5ff]"
                  data-testid="remember-me-checkbox"
                />
                <span className="text-sm text-gray-400">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-[#02b5ff] hover:underline">
                Forgot password?
              </Link>
            </div>

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-gradient-to-r from-[#02b5ff] to-[#0099dd] hover:from-[#0099dd] hover:to-[#0088cc] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <span className="animate-spin mr-2">⏳</span>
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <LogIn className="mr-2 h-5 w-5" />
                  Sign In
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400">
              Don't have an account?{' '}
              <Link 
                to="/register" 
                className="text-[#02b5ff] hover:text-[#02b5ff] font-semibold"
                data-testid="register-link"
              >
                Create one
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-600 text-sm mt-6">
          © BE ELITE APP
        </p>
      </div>
    </div>
  );
}
